import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { removeEmptyValues } from "utils/removeEmptyValues";
import {
  useCreateDenomination,
  useDenominationParents,
  useStructureTypes
} from "../api";
import { createStructureSchema } from "../validation";

// Constants
const INITIAL_FORM_VALUES = {
  name: "",
  acronym: "",
  denomination_structure_type_id: "",
  parent_id: "",
  email: "",
  phone: "",
  p_o_box: "",
  country_id: "",
  region_id: "",
  zone_id: "",
  city_id: "",
  sub_city_id: "",
  woreda_id: "",
  kebele: "",
  house_number: "",
  specific_location: "",
  description: "",
  acceptTerms: false,
  latitude: "",
  longitude: ""
};

export const useCreateStructureForm = () => {
  const { id: denominationId } = useParams();
  const navigate = useNavigate();

  // Form setup
  const {
    register,
    unregister,
    watch,
    setValue,
    control,
    handleSubmit,
    reset,
    formState: { errors, defaultValues }
  } = useForm({
    defaultValues: INITIAL_FORM_VALUES,
    resolver: yupResolver(createStructureSchema)
  });

  // API Hooks
  const denominationMutation = useCreateDenomination({
    redirect: false
  });

  const denominationStructureTypeId = watch("denomination_structure_type_id");

  const { data: denominationsData, isLoading: isLoadingDenominations } =
    useDenominationParents({
      denominationStructureTypeId,
      config: {
        enabled: !!denominationStructureTypeId
      }
    });

  const { data: structureTypesData, isLoading: isLoadingStructureTypes } =
    useStructureTypes({
      denominationId
    });

  // Memoized data
  const parentStructureOptions = useMemo(() => {
    if (!denominationsData?.result) return [];
    return denominationsData?.result.map(denomination => ({
      optionLabel: denomination.name,
      value: denomination.id
    }));
  }, [denominationsData]);

  const structureTypeOptions = useMemo(() => {
    if (!structureTypesData?.result) return [];
    return structureTypesData.result.map(type => ({
      optionLabel: type.name,
      value: type.id
    }));
  }, [structureTypesData]);

  // Form submission handler
  const onSubmit = useCallback(
    values => {
      const data = { ...values, image: values.image?.[0] };
      const formData = new FormData();

      for (let key in data) {
        if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
          formData.append(key, data[key]);
        }
      }

      removeEmptyValues(formData);

      denominationMutation.mutate(
        {
          denominationData: formData
        },
        {
          onSuccess: () => {
            navigate(`/denomination-dashboard/${denominationId}/structures`);
          }
        }
      );
    },
    [denominationMutation, navigate, denominationId]
  );

  // Reset form on success
  useEffect(() => {
    if (denominationMutation.isSuccess) {
      reset({ ...defaultValues });
    }
  }, [denominationMutation.isSuccess, reset, defaultValues]);

  return {
    // Form methods
    register,
    unregister,
    watch,
    setValue,
    control,
    handleSubmit,
    errors,

    // Data
    parentStructureOptions,
    structureTypeOptions,
    isLoadingDenominations,
    isLoadingStructureTypes,

    // Mutation
    denominationMutation,

    // Handlers
    onSubmit
  };
};
